import { TimerWidget } from '@/features/time/timer-widget';
import { NotificationBell } from '@/features/notifications/notification-bell';
import {
  Briefcase,
  CalendarPlus,
  FolderPlus,
  Inbox,
  ListPlus,
  Menu as MenuIcon,
  PanelLeft,
  Plus,
  Search,
  Shapes,
  UserPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { Button } from '@/design-system/components/button';
import { Kbd, modKey } from '@/design-system/components/kbd';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '@/design-system/components/menu';
import { Tooltip } from '@/design-system/components/tooltip';
import { useCan } from '@/lib/session';
import { useRealtimeStatus } from '@/lib/realtime';
import { usePalette } from './command-palette';
import { useCreateDialog } from './create-store';
import { useNavigationClient } from './client-navigation';
import { useSidebar } from './sidebar-store';

const MODE_LABEL = { expanded: 'Compactar menu', compact: 'Ocultar menu', hidden: 'Mostrar menu' } as const;

export function Topbar() {
  const { mode, cycle, setMobileOpen } = useSidebar();
  const openPalette = usePalette((s) => s.setOpen);

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 bg-stage/85 px-3 backdrop-blur-md md:px-4">
      <Button
        variant="ghost"
        size="icon-sm"
        className="md:hidden"
        aria-label="Abrir menu"
        onClick={() => setMobileOpen(true)}
      >
        <MenuIcon />
      </Button>
      <Tooltip
        label={
          <span className="flex items-center gap-2">
            {MODE_LABEL[mode]} <Kbd>[</Kbd>
          </span>
        }
      >
        <Button
          variant="ghost"
          size="icon-sm"
          className="hidden md:inline-flex"
          aria-label={MODE_LABEL[mode]}
          onClick={cycle}
        >
          <PanelLeft />
        </Button>
      </Tooltip>

      <button
        type="button"
        onClick={() => openPalette(true)}
        className="ml-1 flex h-9 w-full max-w-[420px] items-center gap-2.5 rounded-full bg-canvas/50 px-3.5 text-[13.5px] text-fg-subtle shadow-[inset_0_0_0_1px_var(--color-line)] transition-colors hover:text-fg-muted hover:shadow-[inset_0_0_0_1px_var(--color-line-strong)]"
      >
        <Search className="size-4" />
        <span className="flex-1 truncate text-left">Buscar ou ir para…</span>
        <span className="hidden items-center gap-1 sm:flex">
          <Kbd>{modKey}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <TimerWidget />
        <LiveDot />
        <NotificationBell />
        <CreateMenu />
      </div>
    </header>
  );
}

/** Ponto único para criar qualquer coisa. Itens ficam ativos conforme os módulos chegam. */
function CreateMenu() {
  const { clientId } = useNavigationClient();
  const navigate = useNavigate();
  const canInvite = useCan('members:invite');
  const canCreateClient = useCan('clients:write');
  const canCreateProject = useCan('projects:create');
  const canTask = useCan('tasks:write');
  const canEvent = useCan('events:write');
  const showCreate = useCreateDialog((st) => st.show);
  const canCreative = useCan('creative:write');
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="primary" size="sm" pill className="hidden pl-2.5 md:inline-flex">
          <Plus /> <span className="hidden sm:inline">Criar</span>
        </Button>
      </MenuTrigger>
      <MenuContent align="end" className="w-60">
        {canTask && (
          <MenuItem icon={<ListPlus />} onSelect={() => showCreate('task')}>
            Tarefa
          </MenuItem>
        )}
        <MenuItem icon={<Inbox />} onSelect={() => showCreate('request')}>
          Solicitação
        </MenuItem>
        {canCreateProject && (
          <MenuItem
            icon={<FolderPlus />}
            onSelect={() => navigate(clientId ? `/clients/${clientId}/projects?new=1` : '/clients')}
          >
            Projeto
          </MenuItem>
        )}
        {canEvent && (
          <MenuItem icon={<CalendarPlus />} onSelect={() => navigate('/calendar?new=1')}>
            Evento
          </MenuItem>
        )}
        {canCreative && (
          <>
            <MenuItem
              icon={<Shapes />}
              onSelect={() => navigate(clientId ? `/clients/${clientId}/creative/boards?new=1` : '/clients')}
            >
              Board
            </MenuItem>
            <MenuItem
              icon={<Shapes />}
              onSelect={() =>
                navigate(clientId ? `/clients/${clientId}/creative/moodboards?new=1` : '/clients')
              }
            >
              Moodboard
            </MenuItem>
            <MenuItem
              icon={<Shapes />}
              onSelect={() =>
                navigate(clientId ? `/clients/${clientId}/creative/storyboards?new=1` : '/clients')
              }
            >
              Storyboard
            </MenuItem>
          </>
        )}
        {canCreateClient && (
          <MenuItem icon={<Briefcase />} onSelect={() => navigate('/clients?new=1')}>
            Cliente
          </MenuItem>
        )}
        {canInvite && (
          <>
            <MenuSeparator />
            <MenuItem icon={<UserPlus />} onSelect={() => navigate('/settings/members?invite=1')}>
              Convidar membro
            </MenuItem>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

/** Conexão de tempo real ativa: mudanças da equipe aparecem sem recarregar. */
function LiveDot() {
  const live = useRealtimeStatus((s) => s.live);
  return (
    <Tooltip label={live ? 'Ao vivo: mudanças da equipe aparecem na hora' : 'Reconectando…'}>
      <span
        className="hidden h-8 items-center gap-1.5 px-2 text-[12px] text-fg-subtle sm:flex"
        aria-live="polite"
      >
        <span
          className={live ? 'relative size-2 rounded-full bg-signal' : 'size-2 rounded-full bg-fg-subtle'}
        >
          {live && (
            <span className="absolute inset-0 animate-ping rounded-full bg-signal/50 [animation-duration:2.6s]" />
          )}
        </span>
        {live ? 'ao vivo' : 'offline'}
      </span>
    </Tooltip>
  );
}
